import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { filter, from, Observable, of, switchMap } from 'rxjs';
import { JepGraphic, JepGraphicHit, JepViewHit } from '@jeppesen-foreflight/jmkw-types';
import { FeatureDetailService } from '../../../../utilities/feature-detail.service';
import { NavigationLayer } from '../../layer/navigation.layer';
import { FeatureDetail } from '../../typings/features/feature-detail';
import { DisambiguationDialogService } from './disambiguation-dialog.service';
import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import Point from '@arcgis/core/geometry/Point';
import VectorTileGraphicOrigin from '@arcgis/core/graphic/VectorTileGraphicOrigin';
import type { ClickEvent } from "@arcgis/core/views/input/types";
import type { ViewHitTestResult } from "@arcgis/core/views/types";

@Injectable({
  providedIn: 'root'
})
export class NavigationHitTestService {
  private readonly _api = inject(ToolkitApiService);
  private readonly _dialogSvc = inject(DisambiguationDialogService);
  private readonly _logger = inject(NGXLogger);
  private readonly _featureDetailSvc = inject(FeatureDetailService);

  /**
   * Handles the result of a click hit test by processing the response and event.
   * It filters the hit test results, moves the map view to the clicked location,
   * and returns an observable of the feature detail based on the number of hits.
   *
   * If only one result was returned from the filter, then that feature will be
   * returned.  If multiple are returned then the results will be presented to
   * the user for disambiguation and their selection will be returned.
   *
   * @param {ViewHitTestResult} response - The result of the hit test.
   * @param {ClickEvent} event - The event triggered by the view click.
   * @returns {Observable<FeatureDetail>} An observable that emits the feature detail.
   */
  clickResultHandler(
    response: ViewHitTestResult,
    event: ClickEvent
  ): Observable<FeatureDetail> {
    const viewHits: JepGraphicHit[] = this._getFilteredHits(response.results);
    this._logger.trace(`[NavigationHitTestService] hitTest results:`, viewHits);

    return from(
      this._moveMapView(
        new Point({
          latitude: event.mapPoint.latitude ?? 0,
          longitude: event.mapPoint.longitude ?? 0
        })
      )
    ).pipe(
      filter(() => viewHits.length > 0),
      switchMap(() => this._handleHitResults(viewHits, event))
    );
  }

  /**
   * Used in the manual selection of a map feature
   * Handles the result of a hit test by filtering and processing the hit test results.
   *
   * @param {ViewHitTestResult} response - The response object containing the results of the hit test.
   * @param {string} attributeName - The name of the attribute to match against the hit test results.
   * @param {any} attributeValue - The value of the attribute to match against the hit test results.
   * @returns {Observable<FeatureDetail>} An observable that emits the feature detail created from the graphic.
   */
  manualResultHandler(
    response: ViewHitTestResult,
    attributeName: string,
    attributeValue: any
  ): Observable<FeatureDetail> {
    const viewHits: JepGraphicHit[] = this._getFilteredHits(response.results);
    this._logger.trace(`[NavigationHitTestService] hitTest results:`, viewHits);

    const graphic = this._getGraphicFromHits(
      viewHits,
      attributeName,
      attributeValue
    );
    const item: FeatureDetail =
      this._featureDetailSvc.createFromGraphic(graphic);
    this._logger.trace(`[NavigationHitTestService] item:`, item);
    return of(item);
  }

  /**
   * Filters the hit test results to ensure they meet specific criteria.
   *
   * @param {JepViewHit[]} hits - The hit test results to be filtered.
   * @returns {JepGraphicHit[]} Filtered hit test results.
   */
  private _getFilteredHits(hits: JepViewHit[]): JepGraphicHit[] {
    return hits
      .filter((hit): hit is JepGraphicHit => hit.type === 'graphic')
      .filter(this._filterHitTestResults.bind(this));
  }

  /**
   * Moves the map view to the specified coordinates and handles errors.
   *
   * @param {Point} mapPoint - The point to move the map view to.
   * @returns {Promise<void>} A promise that resolves when the map view is moved.
   */
  private _moveMapView(mapPoint: Point): Promise<unknown> {
    return this._api
      .moveMapView(mapPoint.latitude, mapPoint.longitude)
      .catch((error) => {
        if (
          error.name !== 'AbortError' &&
          error.name !== 'view:goto-interrupted'
        ) {
          this._logger.error(error);
        }
      });
  }

  /**
   * Handles the results of the hit test based on the number of hits.
   *
   * @param {JepGraphicHit[]} viewHits - The filtered hit test results.
   * @param {ClickEvent} event - The event triggered by the view click.
   * @returns {Observable<FeatureDetail>} An observable that emits the feature detail.
   */
  private _handleHitResults(
    viewHits: JepGraphicHit[],
    event: ClickEvent
  ): Observable<FeatureDetail> {
    if (viewHits.length === 1) {
      return of(this._featureDetailSvc.createFromGraphic(viewHits[0].graphic));
    } else {
      return this._dialogSvc.handleMultipleFeatureDisambiguation(
        event,
        viewHits
      );
    }
  }

  /**
   * Retrieves the graphic from the hit results based on the attribute name and value.
   *
   * @param {JepGraphicHit[]} viewHits - The filtered hit test results.
   * @param {string} attributeName - The name of the attribute to match against.
   * @param {any} attributeValue - The value of the attribute to match against.
   * @returns {JepGraphic} The graphic that matches the criteria.
   */
  private _getGraphicFromHits(
    viewHits: JepGraphicHit[],
    attributeName: string,
    attributeValue: any
  ): JepGraphic {
    if (viewHits.length === 1) {
      return viewHits[0].graphic;
    }
    return viewHits.find(
      (hit) => hit.graphic.attributes[attributeName] === attributeValue
    )?.graphic as JepGraphic;
  }

  /**
   * Filters the hit test results to ensure they meet specific criteria.
   *
   * @param {JepViewHit} hit - The hit test result to be filtered.
   * @returns {boolean} True if the hit test result is valid, otherwise false.
   */
  private _filterHitTestResults(hit: JepGraphicHit): boolean {
    const navLayer = this._api.getLayerById<NavigationLayer>('navigation');
    const { ifr_theme, tiles_include_attribute_data } = hit?.graphic.attributes || {};

    const layerId = (hit?.graphic?.origin as VectorTileGraphicOrigin)?.layerId;
    if (!layerId) return false;

    const styleIsVisible =
      navLayer?.getStyleLayerVisibility(layerId) === 'visible';
    const layerIsVisible = !!hit?.layer?.visible && styleIsVisible;

    return (
      navLayer?.ifr.toLowerCase() === ifr_theme &&
      tiles_include_attribute_data === 1 &&
      !!layerIsVisible
    );
  }
}
